import { Card, Space, Typography, Button, Spin, Progress } from "antd";
import {
  ReloadOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RiseOutlined,
  TrophyOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import styles from "./TransactionCounter.module.css";

const { Title, Text } = Typography;

const ONE_BILLION = 1_000_000_000;

interface TransactionCounterProps {
  totalTransactions: number;
  totalAmount: number;
  yoyTpv: {
    currentYearToDate: number;
    previousYearToDate: number;
    deltaAmount: number;
    percentageChange: number | null;
    asOf: string;
  } | null;
  lifetimeTpv: number;
  startDate: string | null;
  endDate: string | null;
  loading: boolean;
  isPaused: boolean;
  onRefresh: () => void;
  onPause: () => void;
  onResume: () => void;
}

export default function TransactionCounter({
  totalTransactions,
  totalAmount,
  yoyTpv,
  lifetimeTpv,
  startDate,
  endDate,
  loading,
  isPaused,
  onRefresh,
  onPause,
  onResume,
}: TransactionCounterProps) {
  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatIntegerCurrency = (amount: number): string =>
    new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.floor(amount));

  const formatCompact = (amount: number): string =>
    new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 1,
      notation: "compact",
    }).format(amount);

  const formatTime = (dateString: string | null): string => {
    if (!dateString) return "—";
    const d = dayjs(dateString);
    return d.isValid() ? d.format("HH:mm") : "—";
  };

  const milestonePercent = Math.min(
    (lifetimeTpv / ONE_BILLION) * 100,
    100
  );

  const crossed1B = lifetimeTpv >= ONE_BILLION;

  const yoyPositive =
    yoyTpv?.percentageChange !== null &&
    yoyTpv?.percentageChange !== undefined &&
    yoyTpv.percentageChange >= 0;

  const yoyLabel =
    yoyTpv?.percentageChange == null
      ? null
      : `${yoyPositive ? "+" : ""}${yoyTpv.percentageChange.toFixed(1)}%`;

  const currentYear = new Date().getFullYear();
  const prevYear = currentYear - 1;

  const hasData = totalTransactions > 0 || lifetimeTpv > 0;

  return (
    <div className={styles.counterPanel}>
      <Card className={styles.statsCard} styles={{ body: { padding: "16px" } }}>
        {/* ── Header ─────────────────────────────── */}
        <div className={styles.header}>
          <Title level={5} className={styles.title}>
            Live Transaction Map
          </Title>
          <Space>
            {isPaused ? (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={onResume}
                size="small"
              >
                Play
              </Button>
            ) : (
              <Button
                icon={<PauseCircleOutlined />}
                onClick={onPause}
                size="small"
              >
                Pause
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              loading={loading}
              size="small"
            />
          </Space>
        </div>

        {loading && !hasData ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        ) : !hasData && isPaused ? (
          <div className={styles.pausedMessage}>
            <Text type="secondary">Click Play to start live updates</Text>
          </div>
        ) : (
          <>
            {/* ── Section 1: All-Time Volume (the milestone) ── */}
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <TrophyOutlined className={styles.sectionIcon} />
                <Text className={styles.sectionTitle}>All-Time Volume</Text>
              </div>

              <div className={styles.milestoneValue}>
                <Text
                  className={styles.lifetimeAmount}
                  style={{
                    color: crossed1B ? "#f59e0b" : "#7c3aed",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {lifetimeTpv > 0 ? formatIntegerCurrency(lifetimeTpv) : "—"}
                </Text>
                {crossed1B && (
                  <span className={styles.badge}>🎉 €1B milestone!</span>
                )}
              </div>

              {!crossed1B && lifetimeTpv > 0 && (
                <div className={styles.progressWrapper}>
                  <Progress
                    percent={parseFloat(milestonePercent.toFixed(1))}
                    strokeColor={{ from: "#7c3aed", to: "#f59e0b" }}
                    trailColor="#f0f0f0"
                    size="small"
                    format={(pct) => (
                      <Text style={{ fontSize: 10, color: "#888" }}>
                        {pct}% to €1B
                      </Text>
                    )}
                  />
                </div>
              )}
            </div>

            {/* ── Section 2: Growth momentum ──────────── */}
            {yoyTpv && (
              <div className={styles.section}>
                <div className={styles.sectionLabel}>
                  <RiseOutlined className={styles.sectionIcon} />
                  <Text className={styles.sectionTitle}>Growth</Text>
                  {yoyLabel && (
                    <span
                      className={styles.growthBadge}
                      style={{
                        background: yoyPositive
                          ? "#dcfce7"
                          : "#fee2e2",
                        color: yoyPositive ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {yoyLabel} YoY
                    </span>
                  )}
                </div>

                <div className={styles.comparisonRow}>
                  <div className={styles.comparisonItem}>
                    <Text type="secondary" className={styles.compLabel}>
                      {currentYear} YTD
                    </Text>
                    <Text strong className={styles.compValue}>
                      {formatCompact(yoyTpv.currentYearToDate)}
                    </Text>
                  </div>
                  <div className={styles.comparisonDivider} />
                  <div className={styles.comparisonItem}>
                    <Text type="secondary" className={styles.compLabel}>
                      {prevYear} (same period)
                    </Text>
                    <Text className={styles.compValue} style={{ color: "#888" }}>
                      {formatCompact(yoyTpv.previousYearToDate)}
                    </Text>
                  </div>
                </div>
              </div>
            )}

            {/* ── Section 3: Today (live pulse) ───────── */}
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <ThunderboltOutlined
                  className={styles.sectionIcon}
                  style={{ color: "#f59e0b" }}
                />
                <Text className={styles.sectionTitle}>Today</Text>
                {!isPaused && (
                  <span className={styles.liveDot} title="Live" />
                )}
              </div>

              <div className={styles.todayGrid}>
                <div className={styles.todayItem}>
                  <Text type="secondary" className={styles.compLabel}>
                    Transactions
                  </Text>
                  <Text strong style={{ fontSize: 22, color: "#1890ff" }}>
                    {totalTransactions.toLocaleString("nl-NL")}
                  </Text>
                </div>
                <div className={styles.todayItem}>
                  <Text type="secondary" className={styles.compLabel}>
                    Volume
                  </Text>
                  <Text strong style={{ fontSize: 18, color: "#16a34a" }}>
                    {formatCurrency(totalAmount)}
                  </Text>
                </div>
              </div>

              {startDate && endDate && (
                <div className={styles.timeRange}>
                  <CalendarOutlined style={{ fontSize: 10, color: "#aaa" }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {dayjs(startDate).format("MMM D")} · {formatTime(startDate)} – {formatTime(endDate)}
                  </Text>
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
